import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Mail, Linkedin, Users, Upload, Briefcase } from "lucide-react";
import { useToast } from "../hooks/use-toast";

// Static Mock Data for Team Members
const mockTeamMembers = [
  {
    id: "1",
    name: "Gowtham Nair",
    position: "Chief Executive Officer (CEO)",
    department: "Leadership",
    bio: "Visionary leader driving strategy, growth, and overall business direction.",
    photo_url:
      "https://res.cloudinary.com/dkoljvhc9/image/upload/v1777484226/gowtham_thgpvf.jpg",
    email: "info@maroons.ai",
  },
  {
    id: "2",
    name: "Famin Bin Hameed",
    position: "Chief Operating Officer (COO)",
    department: "Operations",
    bio: "Ensures smooth day-to-day operations and efficient execution across all departments.",
    photo_url:
      "https://res.cloudinary.com/dkoljvhc9/image/upload/v1777481429/FAMIN_FINAL.jpg_wij8fh.jpg",
    email: "info@maroons.ai",
  },
  {
    id: "3",
    name: "Sreelal MS",
    position: "Chief People Officer (CPO)",
    department: "Human Resources",
    bio: "Leads talent strategy, culture, and team development across the organization.",
    photo_url:
      "https://res.cloudinary.com/dkoljvhc9/image/upload/v1777481431/SRELAL_FINAL.jpg_lft7ss.jpg",
    email: "info@maroons.ai",
  },
  {
    id: "4",
    name: "Sarath V B",
    position: "Chief Financial Officer (CFO)",
    department: "Finance",
    bio: "Oversees financial planning, compliance, and sustainable growth strategies.",
    photo_url:
      "https://res.cloudinary.com/dkoljvhc9/image/upload/v1777481428/SHARAT_FINAL.jpg_wmwozt.jpg",
    email: "info@maroons.ai",
  },
  {
    id: "5",
    name: "Gowri Rajesh",
    position: "Chief Technical Officer (CTO)",
    department: "Technology",
    bio: "Drives innovation, product development, and digital transformation initiatives.",
    photo_url:
      "https://res.cloudinary.com/dkoljvhc9/image/upload/v1777481428/GOWRI_FINAL.jpg_qjgsoy.jpg",
    email: "info@maroons.ai",
  },
  {
    id: "6",
    name: "Megha Nair",
    position: "Chief Media Director (CMD)",
    department: "Media & Communications",
    bio: "Leads branding, media strategy, and communication across all platforms.",
    photo_url:
      "https://res.cloudinary.com/dkoljvhc9/image/upload/v1777481427/MEGHA_FINAL.jpg_ie59pm.jpg",
    email: "info@maroons.ai",
  },
  {
    id: "7",
    name: "Favas KK",
    position: "Chief Marketing Officer (CMO)",
    department: "Marketing",
    bio: "Drives marketing strategy, brand growth, and customer acquisition.",
    photo_url:
      "https://res.cloudinary.com/dkoljvhc9/image/upload/v1777481428/FAVAS_NEW.jpg_de170b.jpg",
    email: "info@maroons.ai",
  },
];

// Replace with your actual Google Apps Script Web App URL
const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbwy18P6DysHccMoQiOifyvrBkdTsPB_oQYYRbuutFFqZJd1spR0kIrFVVsZ0ZNIw_qOUw/exec";

const Team = () => {
  const { toast } = useToast();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [careerForm, setCareerForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    resume_name: "",
    resume_mimeType: "",
    resume_base64: "",
    cover_letter: "",
    linkedin: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      setTeamMembers(mockTeamMembers);
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleCareerFormChange = (e) => {
    setCareerForm({
      ...careerForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please upload a resume smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    // Convert file to Base64 to send to Google Sheets/Drive
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64Data = event.target.result.split(",")[1]; // Remove data URL prefix
      setCareerForm({
        ...careerForm,
        resume_name: file.name,
        resume_mimeType: file.type,
        resume_base64: base64Data,
      });

      toast({
        title: "Resume Prepared",
        description: `${file.name} is ready to submit`,
        className: "bg-green-50 border-green-200 text-green-900",
      });
    };
    reader.readAsDataURL(file);
  };

  const handleCareerSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Add formType so your single Google Sheet knows which form this came from
      const payload = {
        formType: "Career Application",
        ...careerForm,
      };

      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        // Sending as text/plain avoids CORS preflight issues from standard web browsers
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast({
          title: "Application Submitted Successfully!",
          description:
            "We'll review your application and get back to you soon.",
          className: "bg-green-50 border-green-200 text-green-900",
        });

        // Reset Form
        setCareerForm({
          name: "",
          email: "",
          phone: "",
          position: "",
          experience: "",
          resume_name: "",
          resume_mimeType: "",
          resume_base64: "",
          cover_letter: "",
          linkedin: "",
        });

        const fileInput = document.getElementById("career-resume");
        if (fileInput) fileInput.value = "";
      } else {
        throw new Error("Network response was not ok");
      }
    } catch (error) {
      toast({
        title: "Submission Failed",
        description:
          "There was a problem submitting your application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="h-16 w-16 border-4 border-[#8C1C1C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">
            Loading our amazing team...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      {/* Hero Section */}
      {/* --- Compact Advanced Hero Section --- */}
      <section className="relative py-6 md:py-10 px-6 md:px-12 lg:px-24 overflow-hidden bg-white border-b border-slate-200 flex items-center justify-center">
        {/* Subtle Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMTQwLDI4LDI4LDAuMDUpIi8+PC9zdmc+')] opacity-70 pointer-events-none"></div>

        {/* Glowing Ambient Orbs for Depth (Scaled down for shorter height) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] bg-gradient-to-b from-[#8C1C1C]/5 to-transparent blur-[80px] rounded-full pointer-events-none"></div>
        <div className="absolute -top-20 -right-20 w-[300px] h-[300px] bg-[#8C1C1C]/[0.03] rounded-full blur-[60px] pointer-events-none"></div>
        <div className="absolute top-20 -left-20 w-[300px] h-[300px] bg-slate-100/80 rounded-full blur-[60px] pointer-events-none"></div>

        <div className="max-w-5xl mx-auto text-center relative z-10 flex flex-col items-center">
          {/* Floating Premium Badge */}
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-md border border-[#8C1C1C]/15 shadow-[0_4px_20px_rgba(140,28,28,0.06)] mb-4 transform hover:-translate-y-0.5 transition-transform duration-300">
            <span className="w-1.5 h-1.5 rounded-full bg-[#8C1C1C] mr-2 animate-pulse shadow-[0_0_8px_rgba(140,28,28,0.5)]"></span>
            <span className="text-[10px] sm:text-xs font-bold text-slate-700 tracking-widest uppercase">
              The Minds Behind The Mission
            </span>
          </div>

          {/* Main Typography with Gradient and Underline Highlights */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-4 tracking-tighter leading-[1.15]">
            Meet Our Exceptional{" "}
            <span className="relative inline-block mt-1">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-[#8C1C1C] to-[#ba2a2a]">
                Leadership Team
              </span>
              {/* Offset decorative underline */}
              <span className="absolute bottom-1.5 left-0 w-full h-2.5 bg-[#8C1C1C]/10 -z-10 transform -rotate-1 skew-x-12"></span>
            </span>
          </h1>

          {/* Refined Subtext */}
          <p className="text-sm md:text-base text-slate-600 leading-relaxed max-w-2xl mx-auto font-medium">
            A passionate collective of strategists, innovators, and industry
            experts dedicated to engineering resilient foundations and driving
            measurable commercial growth for your business.
          </p>

          {/* Elegant Divider */}
          <div className="mt-6 w-16 h-1 bg-gradient-to-r from-transparent via-[#8C1C1C]/30 to-transparent rounded-full"></div>
        </div>
      </section>

      {/* Advanced Team Grid based on Uploaded Reference */}
      <section className="py-8 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="group relative rounded-[2rem] overflow-hidden bg-white shadow-xl hover:shadow-2xl transition-all duration-500 h-[500px]"
              >
                {/* Subtle Background Watermark Layer (Mimics the 'M' graphic) */}
                <div className="absolute inset-0 bg-slate-50 flex items-center justify-center z-0">
                  <div className="text-[18rem] font-black text-slate-100/60 leading-none select-none pointer-events-none transform -rotate-12 group-hover:scale-110 transition-transform duration-700">
                    M
                  </div>
                </div>

                {/* Main Image */}
                <div className="absolute inset-0 z-10 flex items-end justify-center">
                  {member.photo_url ? (
                    <img
                      src={member.photo_url}
                      alt={member.name}
                      className="w-full h-[95%] object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  {/* Fallback Icon */}
                  <div className="w-full h-full items-center justify-center text-slate-400 bg-slate-100 hidden">
                    <Users className="h-20 w-20 text-[#8C1C1C]/20" />
                  </div>
                </div>

                {/* Info Block Overlay - Matching the provided image style */}
                <div className="absolute bottom-0 left-0 right-0 z-20 flex flex-col justify-end">
                  {/* The Maroon Curved Block */}
                  <div className="bg-[#8C1C1C] text-white p-6 md:p-8 rounded-tr-[4rem] sm:rounded-tr-[5rem] relative transform transition-transform duration-500 origin-bottom">
                    {/* Shadow/Gradient for Depth inside the block */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 rounded-tr-[4rem] sm:rounded-tr-[5rem] pointer-events-none"></div>

                    <div className="relative z-30">
                      <h3 className="text-2xl font-bold uppercase tracking-wide mb-1 leading-tight">
                        {member.name}
                      </h3>
                      <p className="text-sm font-medium tracking-wider uppercase text-white/90">
                        {member.position}
                      </p>

                      {/* Expandable Hover Details (Bio & Socials) */}
                      <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-out">
                        <div className="overflow-hidden">
                          <div className="pt-4 mt-4 border-t border-white/20 opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 delay-100">
                            {member.bio && (
                              <p className="text-sm text-white/80 mb-5 leading-relaxed">
                                {member.bio}
                              </p>
                            )}

                            {/* Social Icons inside the maroon card */}
                            <div className="flex space-x-3">
                              {member.email && (
                                <a
                                  href={`mailto:${member.email}`}
                                  className="h-10 w-10 bg-white/10 hover:bg-white text-white hover:text-[#8C1C1C] rounded-full flex items-center justify-center transition-colors duration-300 backdrop-blur-sm"
                                  aria-label="Email"
                                >
                                  <Mail className="h-4 w-4" />
                                </a>
                              )}
                              {member.linkedin && (
                                <a
                                  href={member.linkedin}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="h-10 w-10 bg-white/10 hover:bg-white text-white hover:text-[#8C1C1C] rounded-full flex items-center justify-center transition-colors duration-300 backdrop-blur-sm"
                                  aria-label="LinkedIn"
                                >
                                  <Linkedin className="h-4 w-4" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Team - Career Application */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4">
              Join Our <span className="text-[#8C1C1C]">Team</span>
            </h2>
            <p className="text-xl text-slate-600">
              Be part of a dynamic team that's transforming businesses across
              industries.
            </p>
          </div>

          <Card className="border-slate-100 shadow-2xl rounded-[2rem]">
            <CardContent className="p-8 lg:p-12">
              <form onSubmit={handleCareerSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="career-name"
                      className="font-bold text-slate-700"
                    >
                      Full Name *
                    </Label>
                    <Input
                      id="career-name"
                      name="name"
                      required
                      value={careerForm.name}
                      onChange={handleCareerFormChange}
                      placeholder="e.g. Jane Doe"
                      className="bg-slate-50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="career-email"
                      className="font-bold text-slate-700"
                    >
                      Email Address *
                    </Label>
                    <Input
                      id="career-email"
                      name="email"
                      type="email"
                      required
                      value={careerForm.email}
                      onChange={handleCareerFormChange}
                      placeholder="jane@example.com"
                      className="bg-slate-50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] rounded-xl"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="career-phone"
                      className="font-bold text-slate-700"
                    >
                      Phone Number *
                    </Label>
                    <Input
                      id="career-phone"
                      name="phone"
                      type="tel"
                      required
                      value={careerForm.phone}
                      onChange={handleCareerFormChange}
                      placeholder="+91 XXXXX XXXXX"
                      className="bg-slate-50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="career-position"
                      className="font-bold text-slate-700"
                    >
                      Position Applied For *
                    </Label>
                    <Input
                      id="career-position"
                      name="position"
                      required
                      value={careerForm.position}
                      onChange={handleCareerFormChange}
                      placeholder="e.g., Marketing Manager"
                      className="bg-slate-50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] rounded-xl"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="career-experience"
                      className="font-bold text-slate-700"
                    >
                      Years of Experience *
                    </Label>
                    <Input
                      id="career-experience"
                      name="experience"
                      required
                      value={careerForm.experience}
                      onChange={handleCareerFormChange}
                      placeholder="e.g., 5 years"
                      className="bg-slate-50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="career-linkedin"
                      className="font-bold text-slate-700"
                    >
                      LinkedIn Profile
                    </Label>
                    <Input
                      id="career-linkedin"
                      name="linkedin"
                      value={careerForm.linkedin}
                      onChange={handleCareerFormChange}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className="bg-slate-50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="career-resume"
                    className="font-bold text-slate-700"
                  >
                    Upload Resume *{" "}
                    <span className="text-slate-400 font-normal">
                      (Max 5MB)
                    </span>
                  </Label>
                  <div className="mt-2">
                    <input
                      id="career-resume"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleResumeUpload}
                      required
                      className="block w-full text-sm text-slate-600 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-[#fdf7f7] file:text-[#8C1C1C] hover:file:bg-[#f5eaea] cursor-pointer"
                    />
                  </div>
                  {careerForm.resume_name && (
                    <p className="text-sm font-bold text-green-600 mt-3 flex items-center bg-green-50 p-3 rounded-lg border border-green-100 inline-flex">
                      <Upload className="h-4 w-4 mr-2" />
                      {careerForm.resume_name} prepared
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="career-cover"
                    className="font-bold text-slate-700"
                  >
                    Cover Letter{" "}
                    <span className="text-slate-400 font-normal">
                      (Optional)
                    </span>
                  </Label>
                  <Textarea
                    id="career-cover"
                    name="cover_letter"
                    rows={5}
                    value={careerForm.cover_letter}
                    onChange={handleCareerFormChange}
                    placeholder="Tell us why you'd be a great fit for our team..."
                    className="bg-slate-50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] resize-none rounded-xl"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#8C1C1C] hover:bg-[#6c1616] text-white py-7 text-lg rounded-xl font-bold shadow-lg mt-4 transition-transform active:scale-95"
                >
                  {isSubmitting ? (
                    "Submitting Application..."
                  ) : (
                    <>
                      <Briefcase className="mr-2 h-5 w-5" />
                      Submit Application
                    </>
                  )}
                </Button>

                <p className="text-sm text-slate-500 text-center font-medium mt-4">
                  Applications will be reviewed within 5 business days.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Team;
